import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components/native";
import ValueFilterText from "@components/atoms/Filters/ValueFilter";
import { Pressable } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import ShowMoreButton from "@components/atoms/Filters/ShowMoreButton";
import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import SelectedItemsTitle from "@components/atoms/Filters/SelectedItemsTitle";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useValueFromPayload,
} from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import useResetFilter from "../../../../hooks/useResetFilter";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { ProductCategory } from "@utils/constants";

type TextBoxSectionWithValuesProps = {
  options: Array<any>;
  columns: number;
  selectionType?: string;
  displayedRows?: number;
  sectionTitle: string;
  payloadName: Array<string>;
  value?: Array<any>;
  sectionName?: string;
  selectedStyle: SelectedItemTheme;
  direction?: string;
  minWidth?: number;
  selectedProduct: ProductCategory;
  justifyContent?: string;
  isCollapsible?: boolean;
};

const TextListSectionWithValues = ({
  options = [],
  columns = 2,
  selectionType,
  displayedRows = 2,
  sectionTitle = "",
  payloadName = [],
  sectionName = "",
  selectedStyle,
  direction = "column",
  minWidth = 72,
  selectedProduct = ProductCategory.DIAMOND,
  justifyContent = "flex-start",
  isCollapsible = false,
}: TextBoxSectionWithValuesProps) => {
  const isTouched = useRef(false);
  const typography = useTypographyStore();
  const { containerWidth } = useContext(FilterScreenContext);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const basePayloadKey = Array.isArray(payloadName)
    ? payloadName?.[0]
    : payloadName;
  const baseSectionKey =
    basePayloadKey || sectionTitle || sectionName || "options";
  const safeKey = (value: unknown) =>
    String(value ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.textListWithValues`;

  const value = useValueFromPayload(sectionName, payloadName);

  const previouslySelected = useCallback(() => {
    let selected = options?.filter((option) =>
      value?.includes(option?.search_payload?.[0])
    );
    return selected || [];
  }, [options, selectionType]);

  const [selectedItems, setSelectedItems] = useState<any[]>(
    previouslySelected() || []
  );

  useResetFilter(() => {
    setStartRangeIndex(null);
    setSelectedItems([]);
  });

  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState<boolean>(!displayedRows);
  const theme = useTheme();
  const dispatch = useDispatch();

  const itemsPerRow = Math.floor(containerWidth / (minWidth + SpaceType.Extra));

  const columnsWidth = useMemo(() => {
    const columnWidth = Math.floor(
      (containerWidth -
        (itemsPerRow - 1) * SpaceType.Extra -
        2 * SpaceType.Double) /
        itemsPerRow
    );
    return columnWidth;
  }, [itemsPerRow]);

  useEffect(() => {
    if (!isTouched.current) return;

    if (selectedItems.length === 0 && sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
    } else {
      const payload: any = {};
      payloadName?.forEach((name) => {
        payload[name] = selectedItems
          .map((item) => item?.search_payload)
          .flat();
      });

      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }
    }
  }, [selectedItems]);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      isTouched.current = true;

      if (selectionType === SelectionType.range) {
        if (selectedItems.length === 0) {
          setStartRangeIndex(index);
        }
        if (startRangeIndex === null) {
          setStartRangeIndex(index);
          setSelectedItems([item]);
        } else {
          const minIndex = Math.min(startRangeIndex, index);
          const maxIndex = Math.max(startRangeIndex, index);
          const range = options.slice(minIndex, maxIndex + 1);
          const currentMin = options.indexOf(selectedItems[0]);
          const currentMax = options.indexOf(
            selectedItems[selectedItems.length - 1]
          );
          if (selectedItems.length === 1 && currentMax !== index) {
            setSelectedItems(range);
          } else {
            if (index === currentMin - 1) {
              setSelectedItems([item, ...selectedItems]);
            } else if (index === currentMax + 1) {
              setSelectedItems([...selectedItems, item]);
            } else if (index === currentMin) {
              setStartRangeIndex(index + 1);
              setSelectedItems(selectedItems.slice(1));
            } else if (index === currentMax) {
              setStartRangeIndex(index - 1);
              setSelectedItems(selectedItems.slice(0, -1));
            } else {
              setStartRangeIndex(index);
              setSelectedItems([item]);
            }
          }
        }
      }
    },
    [selectionType, startRangeIndex, options, selectedItems]
  );

  if (!options || !options.length) return <></>;

  return (
    <ParentContainer>
      {!isCollapsible && (
        <TitleSection>
          <VDBText isBold type={typography["H3-M"]} color={theme["core-05"]}>
            {sectionTitle}
          </VDBText>
          {selectedItems.length > 0 && (
            <SelectedItemsTitle
              selectionType={selectionType}
              selectedItems={selectedItems}
            />
          )}
        </TitleSection>
      )}
      {(sectionTitle || selectedItems.length > 0) && (
        <Spacer size={SpaceType.Extra} />
      )}
      <Container direction={direction} justifyContent={justifyContent}>
        {options
          ?.slice(0, showAll ? options.length : displayedRows * columns)
          ?.map((item, index) => {
            const optionKey = safeKey(
              item?.payload_name?.[0] ||
                item?.search_payload?.[0] ||
                item?.name ||
                item?.label ||
                index
            );
            const optionTestId = `${baseTestId}.option.${optionKey}`;
            return (
              <Pressable
                key={index}
                onHoverIn={() => setHoveredIndex(index)}
                onHoverOut={() => setHoveredIndex(null)}
                onPress={() => handleItemSelection(item, index)}
                testID={optionTestId}
                data-testId={optionTestId}
              >
                <FilterButton
                  key={index}
                  children={""}
                  isSelected={selectedItems.includes(item)}
                  value={item.label}
                  width={columnsWidth}
                  selectedStyle={selectedStyle}
                  isHovered={index === hoveredIndex}
                />
              </Pressable>
            );
          })}
      </Container>
      <ShowMoreButton
        options={options}
        columns={columns}
        showAll={showAll}
        onPressFunction={setShowAll}
        displayedRows={displayedRows}
        selectedProduct={selectedProduct}
      />
    </ParentContainer>
  );
};

const Container = styled.View<{ justifyContent: string }>`
  gap: ${SpaceType.Extra}px;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ justifyContent }: { justifyContent: string }) =>
    justifyContent ?? "flex-start"};
`;

const FilterButton = styled(ValueFilterText)<{
  width: number;
}>`
  width: ${({ width }: { width: number }) => width}px;
`;

const TitleSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding-top: ${SpaceType.Extra}px;
`;

const ParentContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
`;

export default memo(TextListSectionWithValues);
